'use client';

import { useRouter } from 'next/navigation';
import { trackDiscoveryCallClick } from '@/lib/analytics';
import { getUserContext } from '@/lib/userContext';
import { getStartSubscriptionRoute, getSubmitTasksRoute, getUnlockSupportRoute, openCalendly } from '@/lib/ctaRouting';

export default function ProcessSection() {
  const router = useRouter();

  const handleBookCall = () => {
    getUserContext()?.then(context => {
      const segment = context?.role || 'unauthenticated';
      trackDiscoveryCallClick(
        segment === 'super_admin' ? 'super_admin' : 
        segment === 'ops_admin' || segment === 'admin' ? 'admin' : 
        segment === 'team' ? 'team' : 
        segment === 'client' ? 'client' : 'unauthenticated',
        '/',
        'process'
      );
    });
    openCalendly();
  };

  const handleStartSubscription = async () => {
    const route = await getStartSubscriptionRoute();
    router?.push(route);
    window.scrollTo(0, 0);
  };

  const handleSubmitTasks = async () => {
    const route = await getSubmitTasksRoute();
    router?.push(route);
  };

  const handleUnlockSupport = async () => {
    const route = await getUnlockSupportRoute();
    router?.push(route);
    window.scrollTo(0, 0);
  };

  return (
    <section 
      className="w-full bg-[#2a0f3a] py-12 sm:py-16 lg:py-24"
      style={{
        backgroundImage: "url('/images/img_image_821x1536.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="w-full max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-12">
        
        {/* Header */}
        <div className="flex flex-col justify-center items-center mb-10 sm:mb-12 lg:mb-16">
          <h2 className="text-[40px] sm:text-[60px] lg:text-[80px] font-bebas font-normal leading-[1.1] text-center text-white uppercase">
            How Purple Cow
          </h2>
          <h3 className="text-[40px] sm:text-[60px] lg:text-[80px] font-bebas font-normal leading-[1.1] text-center text-white uppercase -mt-2 sm:-mt-3 lg:-mt-4">
            Unlimited Works
          </h3>
        </div>

        {/* Process Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-6">
          
          {/* Step 1 */}
          <div className="flex flex-col gap-4 sm:gap-5 justify-start items-center w-full">
            <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-[70px] lg:h-[70px] bg-[#b366d9] rounded-full flex items-center justify-center shadow-lg">
              <span className="text-[24px] sm:text-[28px] lg:text-[32px] font-bebas font-normal leading-[1] text-center text-white">1</span>
            </div>
            
            <h4 className="text-[18px] sm:text-[20px] lg:text-[22px] font-bebas font-normal leading-[1.2] text-center text-white uppercase tracking-wide">
              Apply or Book a Call
            </h4>
            
            <p className="text-[13px] sm:text-[14px] font-poppins font-normal leading-[1.6] text-center text-[#e4e4e4] w-full">
              We learn your goals, workload, brand, and priorities.
            </p>
            
            <a
              href="https://calendly.com/john-smulo/purple-cow-unlimited-discovery-call"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleBookCall}
              className="w-full px-6 py-3.5 bg-transparent text-white text-[11px] sm:text-xs font-medium uppercase tracking-wider rounded-full border-2 border-white/70 hover:bg-white hover:text-[#8923b3] transition-colors mt-auto block text-center"
            >
              BOOK A CALL
            </a>
          </div>

          {/* Step 2 */}
          <div className="flex flex-col gap-4 sm:gap-5 justify-start items-center w-full">
            <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-[70px] lg:h-[70px] bg-[#b366d9] rounded-full flex items-center justify-center shadow-lg">
              <span className="text-[24px] sm:text-[28px] lg:text-[32px] font-bebas font-normal leading-[1] text-center text-white">2</span>
            </div>
            
            <h4 className="text-[18px] sm:text-[20px] lg:text-[22px] font-bebas font-normal leading-[1.2] text-center text-white uppercase tracking-wide">
              Subscribe and Onboard
            </h4>
            
            <p className="text-[13px] sm:text-[14px] font-poppins font-normal leading-[1.6] text-center text-[#e4e4e4] w-full">
              You join your plan and we collect brand assets, tools, and project needs.
            </p>
            
            <button
              onClick={handleStartSubscription}
              className="w-full px-4 py-3.5 bg-transparent text-white text-[11px] sm:text-xs font-medium uppercase tracking-wider rounded-full border-2 border-white/70 hover:bg-white hover:text-[#8923b3] transition-colors mt-auto"
            >
              START YOUR SUBSCRIPTION
            </button>
          </div>

          {/* Step 3 */}
          <div className="flex flex-col gap-4 sm:gap-5 justify-start items-center w-full">
            <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-[70px] lg:h-[70px] bg-[#b366d9] rounded-full flex items-center justify-center shadow-lg">
              <span className="text-[24px] sm:text-[28px] lg:text-[32px] font-bebas font-normal leading-[1] text-center text-white">3</span>
            </div>
            
            <h4 className="text-[18px] sm:text-[20px] lg:text-[22px] font-bebas font-normal leading-[1.2] text-center text-white uppercase tracking-wide">
              Send Unlimited Tasks
            </h4>
            
            <p className="text-[13px] sm:text-[14px] font-poppins font-normal leading-[1.6] text-center text-[#e4e4e4] w-full">
              We work daily on your priorities and you review and approve.
            </p>
            
            <button
              onClick={handleSubmitTasks}
              className="w-full px-4 py-3.5 bg-transparent text-white text-[11px] sm:text-xs font-medium uppercase tracking-wider rounded-full border-2 border-white/70 hover:bg-white hover:text-[#8923b3] transition-colors mt-auto"
            >
              SUBMIT YOUR FIRST TASKS
            </button>
          </div>

          {/* Step 4 */}
          <div className="flex flex-col gap-4 sm:gap-5 justify-start items-center w-full">
            <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-[70px] lg:h-[70px] bg-[#b366d9] rounded-full flex items-center justify-center shadow-lg">
              <span className="text-[24px] sm:text-[28px] lg:text-[32px] font-bebas font-normal leading-[1] text-center text-white">4</span>
            </div>
            
            <h4 className="text-[18px] sm:text-[20px] lg:text-[22px] font-bebas font-normal leading-[1.2] text-center text-white uppercase tracking-wide">
              Scale Creative Output
            </h4>
            
            <p className="text-[13px] sm:text-[14px] font-poppins font-normal leading-[1.6] text-center text-[#e4e4e4] w-full">
              Your marketing finally becomes consistent, reliable, and predictable.
            </p>
            
            <button
              onClick={handleUnlockSupport}
              className="w-full px-3 py-3.5 bg-[#d946ef] text-white text-[10px] sm:text-[11px] font-medium uppercase tracking-wider rounded-full hover:bg-[#e879f9] transition-colors mt-auto"
            >
              UNLOCK UNLIMITED CREATIVE SUPPORT
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
